export type ListingFilters = {
  search: string;
  selectedCategory: string;
  priceMin: number;
  priceMax: number;
  sortBy: string;
  inStockOnly: boolean;
  comingSoonOnly: boolean;
};

export const LISTING_STATE_KEY = 'catalog_listing_browse_state';

export type PersistedListingState = {
  filters: ListingFilters;
  /** Highest page number loaded via infinite scroll */
  page: number;
  scrollY: number;
  updatedAt: number;
};

const DEFAULT_FILTERS: ListingFilters = {
  search: '',
  selectedCategory: '',
  priceMin: 0,
  priceMax: 10000,
  sortBy: 'newest',
  inStockOnly: false,
  comingSoonOnly: false,
};

export function defaultListingFilters(): ListingFilters {
  return { ...DEFAULT_FILTERS };
}

export function readListingState(): PersistedListingState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LISTING_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedListingState;
    if (!parsed?.filters) return null;
    return {
      filters: { ...DEFAULT_FILTERS, ...parsed.filters },
      page: Math.max(1, Number(parsed.page) || 1),
      scrollY: Math.max(0, Number(parsed.scrollY) || 0),
      updatedAt: Number(parsed.updatedAt) || Date.now(),
    };
  } catch {
    return null;
  }
}

export function writeListingState(state: PersistedListingState): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(LISTING_STATE_KEY, JSON.stringify({ ...state, updatedAt: Date.now() }));
  } catch {
    // ignore quota / private mode
  }
}

export function patchListingState(
  patch: Partial<PersistedListingState> & {
    filters?: Partial<ListingFilters>;
  }
): void {
  const current = readListingState() ?? {
    filters: defaultListingFilters(),
    page: 1,
    scrollY: 0,
    updatedAt: Date.now(),
  };
  writeListingState({
    filters: { ...current.filters, ...(patch.filters ?? {}) },
    page: patch.page ?? current.page,
    scrollY: patch.scrollY ?? current.scrollY,
    updatedAt: Date.now(),
  });
}

export function clearListingState(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(LISTING_STATE_KEY);
  } catch {
    // ignore
  }
}

/** Build catalog home URL that restores a category selection */
export function catalogListingHref(options?: {
  categoryId?: string | null;
  subcategoryId?: string | null;
}): string {
  const id = options?.subcategoryId || options?.categoryId;
  if (!id) return '/';
  return `/?category=${encodeURIComponent(id)}`;
}
